import React from "react";
import { useFormikContext } from "formik";
import LinearProgress from "@mui/material/LinearProgress";
import Box from "@mui/material/Box";

import {
  formatAssetInput,
  prepareAssetInputWithCustomFields,
  buildAssetPatch,
  buildAssetBaseValues,
} from "../../utils/asset";
import { useAssets } from "../../contexts/assets";
import useNotification from "../../hooks/notification";
import { usePermissions } from "../../contexts/permissions";
import CaptureActionsMobile from "../../components/capture/CaptureActionsMobile";
import CaptureSkeleton from "../../components/CaptureSkeleton";
import FormFields from "./FormFields";

const styles = {
  stickyActions: {
    width: "100%",
    position: "sticky",
    bottom: 0,
    left: 0,
    zIndex: 99,
    padding: 0,
  },
};

export default function FormPage({
  type,
  baselineRef,
  displayClient,
  displayProject,
  displayWarehouse,
  overrideDisplayFields,
}) {
  const { can } = usePermissions();
  const formProps = useFormikContext();
  const { notifyError, notifySuccess } = useNotification();

  const {
    projectId,
    loading,
    loadingMutation,
    currentAsset,
    updateAsset,
    createAsset,
    createImage,
  } = useAssets();

  const canWrite = can("assets.write");

  const handleSubmitAction = ({
    variables,
    setSubmitting,
    setStatus,
    setErrors,
    submitAction,
    handleSubmit,
    afterSuccess,
  }) => {
    handleSubmit();
    setSubmitting(true);

    return submitAction({
      variables: {
        ...variables,
        input: formatAssetInput(variables.input),
        baseValues: variables.baseValues ? formatAssetInput(variables.baseValues) : undefined,
      },
      onSuccess: response => {
        setSubmitting(false);
        setStatus({ success: !response?.conflict });
        if (afterSuccess) afterSuccess(response);
      },
      onFailure: errors => {
        setStatus({ success: false });
        setErrors(errors);
        notifyError(errors);
        setSubmitting(false);
      },
    });
  };

  const submitUpdateAsset = ({ values, ...props }) => async () => {
    const initialValues = baselineRef?.current?.values || formProps.initialValues;
    const patch = buildAssetPatch(values, initialValues);
    if (Object.keys(patch).length === 0) {
      notifySuccess("Asset was updated");
      return;
    }
    const input = prepareAssetInputWithCustomFields(patch, currentAsset);
    const baseValues = prepareAssetInputWithCustomFields(
      buildAssetBaseValues(patch, initialValues),
      currentAsset
    );
    const variables = {
      id: currentAsset?.id,
      input,
      baseValues,
      expectedVersion: currentAsset?.lockVersion,
    };

    return handleSubmitAction({
      ...props,
      variables,
      submitAction: updateAsset,
      // Mark saved fields clean so they aren't treated as dirty (and re-sent) on
      // the next save once the subscription/collection update flows back in.
      afterSuccess: response => {
        if (!response?.conflict && baselineRef?.current) {
          baselineRef.current = {
            ...baselineRef.current,
            values: { ...baselineRef.current.values, ...patch },
          };
        }
      },
    });
  };

  const submitCreateAsset = ({ values, ...props }) => async () => {
    const { customFields, ...formValues } = values;
    if (values.id) {
      await submitUpdateAsset(formProps)({ values: formValues, ...props });
    }
    const variables = { projectId, input: {} };
    handleSubmitAction({ ...props, variables, submitAction: createAsset });
  };

  const submitDuplicateAsset = ({ values, ...props }) => async () => {
    const { customFields, ...formValues } = values;
    await submitUpdateAsset(formProps)({ values: formValues, ...props });
    const variables = {
      projectId: currentAsset.project.id,
      input: {},
      sourceAssetId: currentAsset.id,
    };
    handleSubmitAction({ ...props, variables, submitAction: createAsset });
  };

  return (
    <form
      id="assetForm"
      className="d-flex flex-column flex-grow-1 justify-content-between justify-content-md-start"
      style={{ position: "relative" }}
    >
      {loading && (
        <Box py={1}>
          <LinearProgress className="mt-2" />
        </Box>
      )}
      {currentAsset?.id ? (
        <FormFields
          type={type}
          asset={currentAsset}
          loading={loadingMutation}
          displayClient={displayClient}
          displayProject={displayProject}
          displayWarehouse={displayWarehouse}
          updateAsset={updateAsset}
          createImage={createImage}
          setErrors={formProps.setErrors}
          errors={formProps.errors}
          overrideDisplayFields={overrideDisplayFields}
        />
      ) : (
        loading && <CaptureSkeleton />
      )}
      <Box mb={2} />
      <Box sx={styles.stickyActions}>
        <CaptureActionsMobile
          disabled={formProps.isSubmitting}
          onSave={submitUpdateAsset(formProps)}
          disableSave={!currentAsset?.id}
          onCreate={submitCreateAsset(formProps)}
          onDuplicate={submitDuplicateAsset(formProps)}
          projectId={projectId}
          canWrite={canWrite}
        />
      </Box>
    </form>
  );
}
